import { useState } from 'react';
import { useListKeyboardNavigation } from '@/shared/hooks/useListKeyboardNavigation';
import type { SelectOption } from '../helpers/types';
import { normalizeOptionValue, toStringArray } from '../helpers/utils';

type UseSelectProps = {
  /** 選択された値 */
  value?: string | number | (string | number)[] | undefined;
  /** 値変更時のコールバック */
  onValueChange?: (value: string | number | (string | number)[]) => void;
  /** フォーカス離脱時のコールバック */
  onBlur?: () => void;
  /** Select のオプション */
  options: SelectOption[];
  /** 固定された下部オプション */
  fixedBottomOptions?: SelectOption[];
  /** 検索機能を有効にする */
  showSearch?: boolean;
  /** 複数選択を有効にする */
  multiple?: boolean;
};

/**
 * Select コンポーネントの共通ロジックを管理するカスタムフック
 */
export const useSelect = ({
  value,
  onValueChange,
  onBlur,
  options,
  fixedBottomOptions,
  showSearch = false,
  multiple = false,
}: UseSelectProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [multiOpen, setMultiOpen] = useState(false);
  const [singleSearchOpen, setSingleSearchOpen] = useState(false);

  // 検索フィルタリング
  const filteredOptions =
    showSearch && searchValue.trim()
      ? options.filter((option) => {
          const text = option.searchLabel ?? (typeof option.label === 'string' ? option.label : '');
          return text.toLowerCase().includes(searchValue.toLowerCase());
        })
      : options;

  // 複数選択モード用
  const selectedValues = toStringArray(value);
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(normalizeOptionValue(option.value)),
  );

  const toggleValue = (optionValue: string | number) => {
    const normalizedValue = normalizeOptionValue(optionValue);
    const current = toStringArray(value);
    const nextValues = current.includes(normalizedValue)
      ? current.filter((v) => v !== normalizedValue)
      : [...current, normalizedValue];

    // 元の型に復元
    const resolvedValues = nextValues.map((v) => {
      const option = options.find((opt) => normalizeOptionValue(opt.value) === v);
      return option?.value ?? v;
    });

    onValueChange?.(resolvedValues);
  };

  // 単一選択モード用
  const selectValue =
    value === undefined || value === null || Array.isArray(value) ? '' : String(value);

  const handleSingleValueChange = (nextValue: string) => {
    const option =
      options.find((opt) => normalizeOptionValue(opt.value) === nextValue) ??
      fixedBottomOptions?.find((opt) => normalizeOptionValue(opt.value) === nextValue);
    // オプションに存在しない値の変更を無視（Radix Selectがoptions切り替え時に値をリセットする問題の対策）
    // クリアボタンはhandleClear経由で直接onValueChangeを呼ぶため影響なし
    if (!option) return;
    onValueChange?.(option.value);
  };

  // キーボードナビゲーション
  const {
    activeIndex,
    handleKeyDown: handleSearchKeyDown,
    resetActiveIndex,
  } = useListKeyboardNavigation({
    itemCount: filteredOptions.length,
    onSelect: (index) => {
      const option = filteredOptions[index];
      if (!option) return;
      if (multiple) {
        // 複数選択: トグルして開いたまま
        toggleValue(option.value);
      } else {
        // 単一選択: 値セット + 閉じる
        handleSingleValueChange(normalizeOptionValue(option.value));
        setSingleSearchOpen(false);
        setSearchValue('');
      }
    },
    onClose: () => {
      if (multiple) {
        handleMultiOpenChange(false);
      } else {
        handleSingleSearchOpenChange(false);
      }
    },
  });

  // 検索値変更時に activeIndex をリセット
  const handleSearchValueChange = (nextSearchValue: string) => {
    setSearchValue(nextSearchValue);
    resetActiveIndex();
  };

  // クリアボタンのハンドラー
  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onValueChange?.(multiple ? [] : '');
  };

  const handleMultiOpenChange = (open: boolean) => {
    setMultiOpen(open);
    if (!open) {
      setSearchValue('');
      resetActiveIndex();
      onBlur?.();
    }
  };

  const handleSingleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
      resetActiveIndex();
      onBlur?.();
    }
  };

  // showSearch=true 時の SingleSelect 用 Popover 開閉制御
  const handleSingleSearchOpenChange = (open: boolean) => {
    setSingleSearchOpen(open);
    if (!open) {
      setSearchValue('');
      resetActiveIndex();
      onBlur?.();
    }
  };

  return {
    // 状態
    searchValue,
    multiOpen,
    filteredOptions,
    activeIndex,

    // 複数選択用
    selectedValues,
    selectedOptions,
    toggleValue,
    handleMultiOpenChange,

    // 単一選択用
    selectValue,
    handleSingleValueChange,
    handleSingleOpenChange,

    // 検索付き単一選択用（Popover ベース）
    singleSearchOpen,
    handleSingleSearchOpenChange,

    // 検索キーボードナビゲーション
    handleSearchKeyDown,

    // 共通
    setSearchValue: handleSearchValueChange,
    handleClear,
  };
};
